/**
 * Accessibility Utilities
 * 
 * This file provides utilities for handling text sizing and accessibility
 * across the app, ensuring that users with visual impairments can use
 * larger text sizes without breaking the UI.
 */

/**
 * Maximum font size multiplier for text that should scale but has size limits.
 * This prevents text from growing so large that it breaks layouts.
 * 
 * Values:
 * - 1.0 = no scaling allowed (use sparingly, only for decorative elements)
 * - 1.3 = limited scaling (good for buttons, navigation)
 * - 1.5 = moderate scaling (good for body text)
 * - 2.0 = generous scaling (good for content-focused text)
 * - undefined/null = unlimited scaling (use for main content)
 */
export const FontSizeMultiplier = {
  NONE: 1.0,        // No scaling - use only for decorative text
  LIMITED: 1.3,     // Limited scaling - buttons, navigation, tight spaces
  MODERATE: 1.5,    // Moderate scaling - labels, secondary text
  GENEROUS: 2.0,    // Generous scaling - body text, content
  UNLIMITED: undefined, // Unlimited - main readable content
} as const;

/**
 * Common text props for different UI element types.
 * Import and spread these into your Text components for consistent behavior.
 * 
 * @example
 * ```tsx
 * <Text {...accessibleTextProps.button}>Button Text</Text>
 * <Text {...accessibleTextProps.label}>Label Text</Text>
 * ```
 */
export const accessibleTextProps = {
  /**
   * For button text - limits scaling and truncates if needed
   */
  button: {
    maxFontSizeMultiplier: FontSizeMultiplier.LIMITED,
    numberOfLines: 1,
    adjustsFontSizeToFit: true,
    minimumFontScale: 0.8,
    allowFontScaling: true,
  },
  
  /**
   * For navigation/footer text - limits scaling, allows wrapping to 2 lines
   */
  navigation: {
    maxFontSizeMultiplier: FontSizeMultiplier.LIMITED,
    numberOfLines: 2,
    adjustsFontSizeToFit: true,
    minimumFontScale: 0.7,
    allowFontScaling: true,
  },
  
  /**
   * For labels and form fields - moderate scaling
   */
  label: {
    maxFontSizeMultiplier: FontSizeMultiplier.MODERATE,
    numberOfLines: 1,
    adjustsFontSizeToFit: true,
    minimumFontScale: 0.8,
    allowFontScaling: true,
  },
  
  /**
   * For body/content text - generous scaling
   */
  body: {
    maxFontSizeMultiplier: FontSizeMultiplier.GENEROUS,
    allowFontScaling: true,
  },
  
  /**
   * For main content text - unlimited scaling
   */
  content: {
    maxFontSizeMultiplier: FontSizeMultiplier.UNLIMITED,
    allowFontScaling: true,
  },
  
  /**
   * For decorative text that should never scale
   * Use sparingly - only for truly decorative elements
   */
  decorative: {
    allowFontScaling: false,
    maxFontSizeMultiplier: FontSizeMultiplier.NONE,
  },
  
  /**
   * For menu items - moderate scaling with single line
   */
  menuItem: {
    maxFontSizeMultiplier: FontSizeMultiplier.MODERATE,
    numberOfLines: 1,
    adjustsFontSizeToFit: true,
    minimumFontScale: 0.85,
    allowFontScaling: true,
  },
} as const;

/**
 * Flexible container styles that adapt to text size changes.
 * Use these when creating buttons or containers that hold text.
 */
export const flexibleContainerStyles = {
  /**
   * Button container that can grow vertically if text is large
   */
  button: {
    minHeight: 40,
    paddingVertical: 10,
    paddingHorizontal: 16,
    flexDirection: 'row' as const,
    alignItems: 'center' as const,
    justifyContent: 'center' as const,
  },
  
  /**
   * Navigation item that can grow if needed
   */
  navigationItem: {
    minHeight: 50,
    paddingVertical: 4,
    alignItems: 'center' as const,
    justifyContent: 'center' as const,
  },
  
  /**
   * Menu item that can accommodate larger text
   */
  menuItem: {
    minHeight: 44, // iOS minimum touch target
    paddingVertical: 8,
    flexDirection: 'row' as const,
    alignItems: 'center' as const,
  },
} as const;

/**
 * Helper function to calculate responsive font size based on base size
 * and user's text size preference.
 * 
 * @param baseSize - The base font size in pixels
 * @param maxMultiplier - Maximum multiplier (from FontSizeMultiplier)
 * @returns The base size (actual scaling happens via Text props)
 */
export function getResponsiveFontSize(
  baseSize: number,
  maxMultiplier: number | undefined = FontSizeMultiplier.MODERATE
): number {
  // Note: The actual scaling is handled by React Native's Text component
  // when allowFontScaling is true and maxFontSizeMultiplier is set.
  // This function just returns the base size for now, but can be extended
  // if you need to do custom calculations.
  return baseSize;
}

/**
 * Accessibility best practices guide:
 * 
 * 1. **Always use maxFontSizeMultiplier** on buttons and navigation items
 *    to prevent layout breaking.
 * 
 * 2. **Use numberOfLines with adjustsFontSizeToFit** on constrained text
 *    (buttons, labels) to gracefully handle overflow.
 * 
 * 3. **Allow unlimited scaling** for main content and body text - this is
 *    the text users most need to read.
 * 
 * 4. **Use minHeight instead of height** on containers so they can grow
 *    vertically when text is larger.
 * 
 * 5. **Test with large text**: iOS Settings > Accessibility > Display & Text Size > Larger Text
 *    Android: Settings > Accessibility > Font size
 * 
 * 6. **Minimum touch target**: Keep all interactive elements at least 44x44
 *    points (iOS) or 48x48dp (Android).
 * 
 * 7. **Use ellipsizeMode** to control how text truncates if it must truncate:
 *    - 'tail' (default) - "This is a long te..."
 *    - 'middle' - "This is...ng text"
 *    - 'head' - "...a long text"
 * 
 * 8. **Avoid allowFontScaling={false}** unless the element is truly decorative
 *    and not meant to be read.
 */

export const accessibilityBestPractices = {
  MINIMUM_TOUCH_TARGET_IOS: 44,
  MINIMUM_TOUCH_TARGET_ANDROID: 48,
  MINIMUM_TOUCH_TARGET: 44, // Use the larger of the two for safety
};

